import type { ReactNode } from 'react';

import { Badge } from '@/components/atoms/Badge';

import { useCohortDecoratedTitle } from './useCohortDecoratedTitle';

import styles from './PageLayout.module.scss';

export interface PageHeaderProps {
    title?: string;
    subtitle?: string;
    subtitleSuffix?: ReactNode;
    /** Short label shown as a pill after the subtitle, e.g. the species in a pen (WB-2207). */
    badge?: string;
    /** Message shown between the heading and the actions. */
    notice?: ReactNode;
    /** Controls on the right side of the title row: exports, toggles, filters. */
    actions?: ReactNode;
    /** Sits immediately after the heading, such as a rename control. */
    titleAction?: ReactNode;
    /** Replaces the heading, such as an inline rename field. */
    titleControl?: ReactNode;
}

export default function PageHeader(props: PageHeaderProps) {
    const { title, subtitle, subtitleSuffix, badge, notice, actions, titleAction, titleControl } =
        props;

    // Names the fish group at cage level without every cage screen having to ask
    // for it. Inert everywhere else, and inert with the flag off — see the hook.
    const displayTitle = useCohortDecoratedTitle(title);

    const hasSubtitle = subtitle != null && subtitle !== '';
    const hasBadge = badge != null && badge !== '';

    return (
        <header className={styles.header}>
            <div className={styles.heading}>
                <div className={styles.titleGroup}>
                    {titleControl ?? <h1 className={styles.title}>{displayTitle ?? ''}</h1>}
                    {titleAction}
                </div>
                {(hasSubtitle || subtitleSuffix != null || hasBadge) && (
                    <div className={styles.subtitle}>
                        {hasSubtitle ? <span>{subtitle}</span> : null}
                        {subtitleSuffix}
                        {hasBadge ? <Badge size="md">{badge}</Badge> : null}
                    </div>
                )}
            </div>

            {notice != null && notice !== false && <div className={styles.notice}>{notice}</div>}
            {actions != null && actions !== false && (
                <div className={styles.actions}>{actions}</div>
            )}
        </header>
    );
}
